/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { type JSX } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  DialogProps,
  IconButton,
  Typography,
} from '@mui/material';
import { styled, SxProps } from '@mui/material/styles';
import { Svgs } from '../assets/svg';
import { useDeviceType } from '../hooks/useDeviceType';
import { Spacing } from '../themes';
import Column from './Column';
import Spacer from './Spacer';
import { Theme } from '@emotion/react';
import { t } from 'i18next';

export const BootstrapDialog: React.ComponentType<DialogProps> = styled(Dialog)(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: '10px',
  },
  '& .MuiDialogContent-root': {
    padding: theme.spacing(1),
    margin: '0px 20px 20px 20px',
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(1),
    justifyContent: 'space-between',
  },
  '& .MuiIconButton-root': {
    position: 'absolute',
    right: 8,
    top: 8,
    color: theme.palette.grey[500],
  },
  '& .browser-dialog': {
    margin: '0px 20px 20px 20px',
  },
}));

interface DialogTitleProps {
  id: string;
  onClose?: () => void;
}

export function BootstrapHeader(props: DialogTitleProps) {
  const { onClose } = props;
  return (
    <IconButton
      aria-label='close'
      onClick={onClose}
      style={{
        visibility: onClose ? 'visible' : 'hidden',
        marginRight: Spacing.xs,
        marginTop: Spacing.xs,
        zIndex: 999,
      }}>
      <Svgs.ActionSmallCloseDefault />
    </IconButton>
  );
}

interface CustomizedDialogsProps extends React.PropsWithChildren {
  title?: string | JSX.Element;
  titleStyle?: SxProps<Theme>;
  dialogSx?: SxProps<Theme>;
  dialogClassName?: string;
  handleSave?: (data?: any) => void;
  handleCancel?: () => void;
  onClose?: () => void;
  data?: any;
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  primaryButtonColor?:
    | 'accent'
    | 'inherit'
    | 'primary'
    | 'secondary'
    | 'success'
    | 'info'
    | 'warning'
    | 'error'
    | 'tertiary'
    | undefined;
  primaryButtonText?: string;
  primaryButtonVariant?:
    | 'link'
    | 'text'
    | 'tertiary'
    | 'outlined'
    | 'contained'
    | 'ghost'
    | 'activeGhost'
    | undefined;
  primaryButtonDisabled?: boolean;
  secondaryButtonColor?:
    | 'accent'
    | 'inherit'
    | 'primary'
    | 'secondary'
    | 'success'
    | 'info'
    | 'warning'
    | 'error'
    | 'tertiary'
    | undefined;
  secondaryButtonVariant?:
    | 'link'
    | 'text'
    | 'tertiary'
    | 'outlined'
    | 'contained'
    | 'ghost'
    | 'activeGhost'
    | undefined;
  secondaryButtonText?: string;
  swapButtonFunctionality?: boolean;
  hideTitleTopSpacer?: boolean;
  hideTitleBottomSpacer?: boolean;
}

const CustomizedDialogs: React.FC<CustomizedDialogsProps> = (props) => {
  const {
    title,
    titleStyle,
    dialogSx,
    dialogClassName,
    handleSave,
    handleCancel,
    onClose,
    open,
    setOpen,
    data,
    primaryButtonColor,
    primaryButtonText,
    primaryButtonVariant,
    primaryButtonDisabled,
    secondaryButtonColor,
    secondaryButtonVariant,
    secondaryButtonText,
    swapButtonFunctionality,
    hideTitleTopSpacer,
    hideTitleBottomSpacer,
  } = props;
  const { isMobile } = useDeviceType();
  const buttonHeight = '38px';

  const closeModal = () => {
    if (onClose) {
      onClose();
    }
    setOpen(false);
  };

  const onCancel = () => {
    if (handleCancel) {
      handleCancel();
    }
    closeModal();
  };

  const onSave = () => {
    if (!handleSave) return;
    if (data) {
      handleSave(data);
    } else {
      handleSave();
    }
    closeModal();
  };

  return (
    <BootstrapDialog
      onClose={closeModal}
      aria-labelledby='customized-dialog-title'
      open={open}
      className={dialogClassName}
      sx={dialogSx}>
      {!isMobile && <BootstrapHeader id='customized-dialog-title' onClose={closeModal} />}
      {!hideTitleTopSpacer && <Spacer height={isMobile ? 'xxs' : 'sm'} />}
      <DialogContent>
        {!title ? null : typeof title === 'string' ? (
          isMobile ? (
            <Typography
              sx={{ textAlign: 'center', display: 'grid', ...titleStyle }}
              variant='p18Bold'
              color='secondary.main'>
              {title}
            </Typography>
          ) : (
            <Typography variant='p24Bold' color='secondary.main' sx={titleStyle}>
              {title}
            </Typography>
          )
        ) : (
          title
        )}

        {!hideTitleBottomSpacer && <Spacer height={isMobile ? 'xs' : 'sm'} />}
        {props.children}
      </DialogContent>
      {!handleSave ? null : isMobile ? (
        <DialogActions>
          <Column style={{ width: '100%' }}>
            <Button
              style={{ height: buttonHeight }}
              color='primary'
              autoFocus
              onClick={swapButtonFunctionality ? onCancel : onSave}>
              {t(primaryButtonText ? primaryButtonText : `Yes`)}
            </Button>
            <Spacer height='xxs' />
            <Button
              style={{ height: buttonHeight }}
              color={secondaryButtonColor ?? 'secondary'}
              variant='outlined'
              onClick={swapButtonFunctionality ? onSave : onCancel}>
              {t(secondaryButtonText ? secondaryButtonText : `Cancel`)}
            </Button>
          </Column>
        </DialogActions>
      ) : (
        <DialogActions className='browser-dialog'>
          <Button
            style={{ height: buttonHeight }}
            color={secondaryButtonColor ?? 'primary'}
            variant={secondaryButtonVariant ?? 'outlined'}
            onClick={swapButtonFunctionality ? onSave : onCancel}>
            <Typography variant='p16'>
              {t(secondaryButtonText ? secondaryButtonText : `Cancel`)}
            </Typography>
          </Button>
          <Button
            style={{ height: buttonHeight }}
            color={primaryButtonColor ?? 'primary'}
            variant={primaryButtonVariant}
            disabled={primaryButtonDisabled}
            autoFocus
            onClick={swapButtonFunctionality ? onCancel : onSave}>
            <Typography variant='p16'>
              {t(primaryButtonText ? primaryButtonText : `Yes`)}
            </Typography>
          </Button>
        </DialogActions>
      )}
    </BootstrapDialog>
  );
};

export default CustomizedDialogs;
